/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { breakpoints } from '@coursera/cds-core';
import { FullscreenExitIcon, FullscreenIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/premium-hub';

const AUTOHIDE_DELAY = 3000;
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

const displayTime = (timeInSeconds: number): string => {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = `${Math.floor((timeInSeconds % 3600) / 60)}`;
  const seconds = `${Math.floor(timeInSeconds % 60)}`.padStart(2, '0');
  if (hours) return `${hours}:${minutes.padStart(2, '0')}:${seconds}`;
  return `${minutes}:${seconds}`;
};

type VideoControlBarProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  hasTextTrack?: boolean;
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const styles = {
  wrapper: (visible: boolean) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    animation: ${visible ? fadeIn : fadeOut} 200ms ease forwards;
    pointer-events: ${visible ? 'auto' : 'none'};
  `,

  gradient: css`
    background: linear-gradient(transparent, var(--cds-color-darken-700));
    padding: var(--cds-spacing-200) var(--cds-spacing-100) var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-100) var(--cds-spacing-50) var(--cds-spacing-100);
    }
  `,

  progressBar: css`
    position: relative;
    width: 100%;
    height: 4px;
    background: var(--cds-color-lighten-300);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--cds-color-white-0);
      outline-offset: 1px;
      background: var(--cds-color-lighten-500);
    }
  `,

  progressFilled: css`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: var(--cds-color-white-0);
    pointer-events: none;
  `,

  timeLabel: css`
    color: var(--cds-color-white-0);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    user-select: none;
  `,

  controlsRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    padding: 0 var(--cds-spacing-50);

    ${breakpoints.down('sm')} {
      gap: 2px;
    }
  `,

  spacer: css`
    flex: 1;
  `,

  controlButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: none;
    border: none;
    border-radius: 4px;
    color: var(--cds-color-white-0);
    cursor: pointer;
    padding: 0;
    position: relative;
    outline: none;

    ${breakpoints.down('sm')} {
      width: 36px;
      height: 36px;
    }

    &:hover {
      background-color: var(--cds-color-lighten-200);
    }

    &:focus-visible {
      box-shadow: inset 0 0 0 2px var(--cds-color-white-0);
      background-color: var(--cds-color-lighten-300);
    }
  `,

  volumeGroup: css`
    display: flex;
    align-items: center;
    flex-direction: row-reverse;

    &:hover > input[type='range'],
    &:focus-within > input[type='range'] {
      width: 60px;
      opacity: 1;
      margin-right: var(--cds-spacing-50);
    }
  `,

  volumeSlider: css`
    -webkit-appearance: none;
    appearance: none;
    width: 0;
    height: 4px;
    opacity: 0;
    margin-right: 0;
    background: var(--cds-color-lighten-300);
    border-radius: 2px;
    outline: none;
    cursor: pointer;
    transition: width 200ms ease, opacity 200ms ease, margin-right 200ms ease;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: var(--cds-color-white-0);
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: var(--cds-color-white-0);
      border: none;
      cursor: pointer;
    }

    &:focus-visible {
      outline: 3px solid var(--cds-color-white-0);
      outline-offset: 2px;
    }
  `,

  moreMenuContainer: css`
    position: relative;
  `,

  moreMenu: css`
    position: absolute;
    bottom: 100%;
    right: 0;
    margin-bottom: var(--cds-spacing-100);
    background: var(--cds-color-darken-900);
    border-radius: 8px;
    padding: var(--cds-spacing-100) 0;
    min-width: 180px;
    box-shadow: 0 2px 12px var(--cds-color-darken-400);
    z-index: 3;
  `,

  menuHeading: css`
    color: var(--cds-color-lighten-700);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    margin: 0;
  `,

  menuItem: css`
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    background: none;
    border: none;
    color: var(--cds-color-white-0);
    font-size: 13px;
    cursor: pointer;
    text-align: left;
    outline: none;

    &:hover {
      background-color: var(--cds-color-lighten-100);
    }

    &:focus-visible {
      box-shadow: inset 0 0 0 2px var(--cds-color-white-0);
      background-color: var(--cds-color-lighten-300);
    }
  `,

  menuItemActive: css`
    font-weight: 700;

    &::before {
      content: '\\2713';
      margin-right: var(--cds-spacing-100);
    }
  `,

  menuDivider: css`
    border: none;
    border-top: 1px solid var(--cds-color-lighten-200);
    margin: var(--cds-spacing-50) 0;
  `,
};

const MoreDotsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="5" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="19" r="2" fill="currentColor" />
  </svg>
);

const PlayFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <polygon points="7,4 17,10 7,16" fill="currentColor" />
  </svg>
);

const PauseFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="5" y="4" width="3.5" height="12" rx="0.5" fill="currentColor" />
    <rect x="11.5" y="4" width="3.5" height="12" rx="0.5" fill="currentColor" />
  </svg>
);

const VolumeMuteFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3,7 L6,7 L10,3 L10,17 L6,13 L3,13 Z" fill="currentColor" />
    <line x1="13" y1="7" x2="18" y2="13" stroke="currentColor" strokeWidth="1.5" />
    <line x1="18" y1="7" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const VolumeFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3,7 L6,7 L10,3 L10,17 L6,13 L3,13 Z" fill="currentColor" />
    <path d="M12,5 A7,7 0 0 1 12,15" stroke="currentColor" fill="none" strokeWidth="1.5" />
    <path d="M14,2.5 A10,10 0 0 1 14,17.5" stroke="currentColor" fill="none" strokeWidth="1.5" />
  </svg>
);

const VolumeIcon = ({ volume, muted }: { volume: number; muted: boolean }) => {
  if (muted || volume === 0) return <VolumeMuteFilledIcon />;
  return <VolumeFilledIcon />;
};

const VideoControlBar = ({ videoRef, hasTextTrack = false }: VideoControlBarProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const resetHideTimer = useCallback<() => void>(() => {
    setVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!menuOpen) setVisible(false);
    }, AUTOHIDE_DELAY);
  }, [menuOpen]);

  // Sync video state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDurationChange = () => setDuration(video.duration || 0);
    const onVolumeChange = () => {
      setMuted(video.muted);
      setVolume(video.volume);
    };
    const onRateChange = () => setPlaybackRate(video.playbackRate);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('seeked', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('loadedmetadata', onDurationChange);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('ratechange', onRateChange);

    // Init from current state
    setIsPlaying(!video.paused);
    setCurrentTime(video.currentTime);
    setDuration(video.duration || 0);
    setMuted(video.muted);
    setVolume(video.volume);
    setPlaybackRate(video.playbackRate);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('seeked', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('loadedmetadata', onDurationChange);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('ratechange', onRateChange);
    };
  }, [videoRef]);

  // Auto-hide timer + listen for mousemove/click on the entire video container
  useEffect(() => {
    resetHideTimer();
    const container = videoRef.current?.parentElement;
    if (container) {
      container.addEventListener('mousemove', resetHideTimer);
    }
    return () => {
      clearTimeout(hideTimerRef.current);
      if (container) {
        container.removeEventListener('mousemove', resetHideTimer);
      }
    };
  }, [resetHideTimer, videoRef]);

  // Fullscreen change listener
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  // Keep controls visible while menu is open
  useEffect(() => {
    if (menuOpen) {
      clearTimeout(hideTimerRef.current);
      setVisible(true);
    } else {
      resetHideTimer();
    }
  }, [menuOpen, resetHideTimer]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const seekToPosition = (clientX: number) => {
    const video = videoRef.current;
    const bar = progressBarRef.current;
    if (!video || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    video.currentTime = fraction * duration;
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    seekToPosition(e.clientX);
    const onMouseMove = (moveEvent: MouseEvent) => {
      seekToPosition(moveEvent.clientX);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    video.muted = newVolume === 0;
  };

  const toggleFullscreen = () => {
    const container = wrapperRef.current?.parentElement;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  };

  const toggleCaptions = () => {
    const video = videoRef.current;
    if (!video || !video.textTracks.length) return;
    const track = video.textTracks[0];
    if (track) {
      const newMode = track.mode === 'showing' ? 'disabled' : 'showing';
      track.mode = newMode;
      setCaptionsEnabled(newMode === 'showing');
    }
  };

  const handlePlaybackRate = (rate: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  // Focus the first menu item when the menu opens
  useEffect(() => {
    if (menuOpen) {
      // Small delay to allow the menu to render
      const timeout = setTimeout(() => {
        const firstItem = menuRef.current?.querySelector<HTMLElement>(
          '[role="menuitemradio"], [role="menuitem"], [role="menuitemcheckbox"]'
        );
        firstItem?.focus();
      }, 0);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [menuOpen]);

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitemradio"], [role="menuitem"], [role="menuitemcheckbox"]'
      ) ?? []
    );
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[next]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prev]?.focus();
        break;
      }
      case 'Home': {
        e.preventDefault();
        items[0]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      }
      case 'Tab': {
        // Prevent Tab from moving focus within the menu; close it instead
        e.preventDefault();
        setMenuOpen(false);
        menuButtonRef.current?.focus();
        break;
      }
      default:
        break;
    }
  };

  const togglePiP = async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch {
      // PiP may not be supported
    }
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  const handleToolbarKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    // Don't intercept keys when inside the menu, volume slider, or progress bar
    // (progress bar has its own arrow key handler with Home/End/PageUp/PageDown)
    const target = e.target as HTMLElement;
    if (target.closest('[role="menu"]') || target.tagName === 'INPUT' || target.getAttribute('role') === 'slider')
      return;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        e.stopPropagation();
        video.currentTime = Math.min(duration, video.currentTime + 5);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        e.stopPropagation();
        video.currentTime = Math.max(0, video.currentTime - 5);
        break;
      case ' ':
      case 'k':
        // Space/K to toggle play (common video player shortcut)
        // Don't intercept if focus is on a button (Space activates buttons natively)
        if (target.tagName === 'BUTTON') return;
        e.preventDefault();
        e.stopPropagation();
        togglePlay();
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={wrapperRef}
      css={styles.wrapper(visible)}
      onMouseMove={resetHideTimer}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onFocus={resetHideTimer}
      onKeyDown={handleToolbarKeyDown}
      role="toolbar"
      aria-label={_t('Video controls')}
    >
      <div css={styles.gradient}>
        {/* Controls row: Play | Time | spacer | Volume | Fullscreen | More */}
        <div css={styles.controlsRow}>
          <button
            type="button"
            css={styles.controlButton}
            onClick={togglePlay}
            aria-label={isPlaying ? _t('Pause') : _t('Play')}
          >
            {isPlaying ? <PauseFilledIcon /> : <PlayFilledIcon />}
          </button>

          <span css={styles.timeLabel}>
            {displayTime(currentTime)} / {displayTime(duration)}
          </span>

          <div css={styles.spacer} />

          <div css={styles.volumeGroup}>
            <button
              type="button"
              css={styles.controlButton}
              onClick={toggleMute}
              aria-label={muted ? _t('Unmute') : _t('Mute')}
            >
              <VolumeIcon volume={volume} muted={muted} />
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={handleVolumeChange}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              css={styles.volumeSlider}
              aria-label={_t('Volume')}
              aria-valuenow={Math.round((muted ? 0 : volume) * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          <button
            type="button"
            css={styles.controlButton}
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? _t('Exit fullscreen') : _t('Enter fullscreen')}
          >
            {isFullscreen ? (
              <FullscreenExitIcon size="large" color="invert" />
            ) : (
              <FullscreenIcon size="large" color="invert" />
            )}
          </button>

          <div css={styles.moreMenuContainer} ref={menuRef}>
            <button
              type="button"
              ref={menuButtonRef}
              id="more-options-button"
              css={styles.controlButton}
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={_t('More options')}
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <MoreDotsIcon />
            </button>

            {menuOpen && (
              <div
                css={styles.moreMenu}
                role="menu"
                aria-labelledby="more-options-button"
                onKeyDown={handleMenuKeyDown}
                tabIndex={-1}
              >
                <div role="group" aria-labelledby="playback-speed-label">
                  <div id="playback-speed-label" css={styles.menuHeading}>
                    {_t('Playback speed')}
                  </div>
                  {PLAYBACK_SPEEDS.map((rate) => {
                    const label = rate === 1 ? _t('Normal') : `${rate}x`;
                    const ariaLabel =
                      rate === 1 ? _t('Playback speed: Normal') : _t('Playback speed: #{rate}x', { rate });
                    return (
                      <button
                        key={rate}
                        type="button"
                        css={[styles.menuItem, rate === playbackRate && styles.menuItemActive]}
                        onClick={() => handlePlaybackRate(rate)}
                        role="menuitemradio"
                        aria-checked={rate === playbackRate}
                        aria-label={ariaLabel}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {hasTextTrack && (
                  <>
                    <hr css={styles.menuDivider} />
                    <button
                      type="button"
                      css={[styles.menuItem, captionsEnabled && styles.menuItemActive]}
                      onClick={() => {
                        toggleCaptions();
                        setMenuOpen(false);
                        menuButtonRef.current?.focus();
                      }}
                      role="menuitemcheckbox"
                      aria-checked={captionsEnabled}
                    >
                      {_t('Captions')}
                    </button>
                  </>
                )}

                {document.pictureInPictureEnabled && (
                  <>
                    <hr css={styles.menuDivider} />
                    <button type="button" css={styles.menuItem} onClick={togglePiP} role="menuitem">
                      {_t('Picture-in-Picture')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar: full-width, played portion in white */}
      <div
        ref={progressBarRef}
        css={styles.progressBar}
        onMouseDown={handleProgressMouseDown}
        role="slider"
        tabIndex={0}
        aria-label={_t('Seek video')}
        aria-valuenow={Math.round(currentTime)}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuetext={`${displayTime(currentTime)} ${_t('of')} ${displayTime(duration)}`}
        onKeyDown={(e) => {
          const video = videoRef.current;
          if (!video) return;
          switch (e.key) {
            case 'ArrowRight':
              e.preventDefault();
              e.stopPropagation();
              video.currentTime = Math.min(duration, video.currentTime + 5);
              break;
            case 'ArrowLeft':
              e.preventDefault();
              e.stopPropagation();
              video.currentTime = Math.max(0, video.currentTime - 5);
              break;
            case 'Home':
              e.preventDefault();
              e.stopPropagation();
              video.currentTime = 0;
              break;
            case 'End':
              e.preventDefault();
              e.stopPropagation();
              video.currentTime = duration;
              break;
            case 'PageUp':
              e.preventDefault();
              e.stopPropagation();
              video.currentTime = Math.min(duration, video.currentTime + 15);
              break;
            case 'PageDown':
              e.preventDefault();
              e.stopPropagation();
              video.currentTime = Math.max(0, video.currentTime - 15);
              break;
            default:
              break;
          }
        }}
      >
        <div css={styles.progressFilled} style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }} />
      </div>
    </div>
  );
};

export default VideoControlBar;
